import { useState, useEffect } from "react";
import { Box, Chip, Tooltip, IconButton, Badge } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import { formatDate, formatTime } from "../../config/formatters.ts";
import WashImageDialog from "./WashImageDialog.tsx";
import CompleteWashDialog from "./CompleteWashDialog.tsx";
import PaymentDialog from "./PaymentDialog.tsx";
import RevertWashDialog from "./RevertWashDialog.tsx";
import EditPaymentDialog from "./EditPaymentDialog.tsx";
import DeleteWashDialog from "./DeleteWashDialog.tsx";
import TabsWrapper from "../../shared/TabsWrapper/index.tsx";
import DateRangeSelector from "../../shared/DateRangeSelector/index.tsx";
import AutoCompleteMultiSelect from "../../shared/AutoCompleteMultiSelect/index.tsx";
import customerTypes from "../../shared/customerTypes.json";
import dayjs, { Dayjs } from "dayjs";

interface Wash {
  id: number;
  customerId: number;
  vehicleId: number;
  masterServiceId: number[];
  vehicleArrivedDate: string;
  vehicleArrivedTime: string;
  actualAmount: number;
  payedAmount: number;
  isOfferApplied: number;
  offerAmount: number;
  remarks: string;
  washStatus: string;
  paymentStatus: string;
  vehicle: {
    id: number;
    regNo: string;
    vehicleDetails: string;
    customer: {
      id: number;
      name: string;
      phone: string;
    };
  };
}

export default function WashesList() {
  const navigate = useNavigate();
  const [washes, setWashes] = useState<Wash[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState("pending");
  const [imageDialogId, setImageDialogId] = useState<number | null>(null);
  const [completeWash, setCompleteWash] = useState<any | null>(null);
  const [paymentWash, setPaymentWash] = useState<any | null>(null);
  const [revertWash, setRevertWash] = useState<any | null>(null);
  const [editPaymentWash, setEditPaymentWash] = useState<any | null>(null);
  const [deleteWash, setDeleteWash] = useState<any | null>(null);
  const [dateRange, setDateRange] = useState<{
    from: Dayjs | null;
    to: Dayjs | null;
  }>({
    from: dayjs(),
    to: dayjs(),
  });
  const [customerTypeFilter, setCustomerTypeFilter] =
    useState<any[]>(customerTypes);

  useEffect(() => {
    getWashes();
  }, [
    page,
    limit,
    search,
    tabValue,
    dateRange.from,
    dateRange.to,
    customerTypeFilter,
  ]);

  const handleDateRangeChange = (range: { from: Dayjs | null; to: Dayjs | null }) => {
    setDateRange(range);
    setPage(0);
  };

  const getWashes = async () => {
    setLoading(true);
    const params: Record<string, any> = {
      search,
      limit,
      offset: page * limit,
      orderColumn: "vehicleArrivedDate",
      orderDirection: "asc",
    };
    if (dateRange.from) params.fromDate = dateRange.from.format("YYYY-MM-DD");
    if (dateRange.to) params.toDate = dateRange.to.format("YYYY-MM-DD");
    if (tabValue === "pending") params.washStatus = "Pending";
    else if (tabValue === "completed") params.washStatus = "Completed";
    else if (tabValue === "payment-pending") params.paymentStatus = "Pending";
    params.customerType = customerTypeFilter
      .map((type: any) => type.value)
      .join(",");
    const response = await request.get("/api/vehicleWashlist", params);
    setWashes(response.data.data);
    setTotalCount(response.data.count);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <>
          <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
            {page * limit + index + 1}
          </Box> &nbsp;
          <>
            {_row?.remarks ? (
              <Tooltip title={_row.remarks} arrow placement="top">
                <WarningAmberIcon
                  sx={{ fontSize: 18, color: "#f59e0b", flexShrink: 0 }}
                />
              </Tooltip>
            ) : null}
          </>
        </>
      ),
    },
    {
      id: "regNo",
      label: "Reg No",
      minWidth: 130,
      format: (_value, row) => (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ fontWeight: 600, color: "#1c1b1f" }}>
            {row?.vehicle?.regNo || "-"}
          </Box>
          {row?.vehicle?.vehicleDetails && (
            <Box sx={{ fontSize: "0.7rem", color: "#64748b" }}>
              {row.vehicle.vehicleDetails}
            </Box>
          )}
        </Box>
      ),
    },
    {
      id: "customerName",
      label: "Customer",
      minWidth: 150,
      format: (_value, row) => row?.customer?.name || "-",
    },
    {
      id: "customerPhone",
      label: "Phone",
      minWidth: 120,
      format: (_value, row) => row?.customer?.phone || "-",
    },
    {
      id: "services",
      label: "Services",
      minWidth: 100,
      align: "center",
      format: (_value, row) => {
        const count = row?.masterServiceId?.length || 0;
        const services = row?.masterServices || [];
        return (
          <Tooltip
            title={
              <Box>
                {services.map((s: any) => (
                  <Box
                    key={s.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      py: 0.25,
                    }}
                  >
                    <Box
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        background: "#2563eb",
                        flexShrink: 0,
                      }}
                    />
                    {s.serviceName}
                  </Box>
                ))}
              </Box>
            }
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  lineHeight: 1.6,
                  px: 1.5,
                  py: 1,
                },
              },
            }}
          >
            <Chip
              label={`${count} Service${count > 1 ? "s" : ""}`}
              size="small"
              sx={{
                background: "rgba(37, 99, 235, 0.1)",
                color: "#2563eb",
                fontWeight: 600,
                fontSize: "0.75rem",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      id: "vehicleArrivedDate",
      label: "Arrival Date",
      minWidth: 120,
      format: (value) => formatDate(value),
    },
    {
      id: "vehicleArrivedTime",
      label: "Arrival Time",
      minWidth: 100,
      format: (value) => formatTime(value),
    },
    {
      id: "actualAmount",
      label: "Amount",
      minWidth: 130,
      align: "right",
      format: (_value, row) => {
        const actual = row?.actualAmount;
        const payed = row?.payedAmount;
        const offer = row?.offerAmount;
        if (actual == null) return "-";
        const actualStr = `₹${Number(actual).toLocaleString()}`;
        if (payed != null && Number(payed) !== Number(actual)) {
          return (
            <Box>
              <Box
                sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#1c1b1f" }}
              >
                {actualStr}
              </Box>
              <Box
                sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#10b981" }}
              >
                Paid: ₹{Number(payed).toLocaleString()}
              </Box>
              {offer && Number(offer) > 0 && (
                <Box
                  sx={{
                    fontSize: "0.65rem",
                    color: "#f59e0b",
                    fontWeight: 600,
                  }}
                >
                  Offer: -₹{Number(offer).toLocaleString()}
                </Box>
              )}
            </Box>
          );
        }
        return (
          <Box sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#1c1b1f" }}>
            {actualStr}
          </Box>
        );
      },
    },
    {
      id: "washStatus",
      label: "Wash Status",
      minWidth: 150,
      align: "center",
      format: (_value, row) => {
        const status = row?.washStatus || "Pending";
        const colors: Record<string, { bg: string; color: string }> = {
          Completed: { bg: "rgba(16, 185, 129, 0.1)", color: "#10b981" },
          Pending: { bg: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" },
          "In Progress": { bg: "rgba(37, 99, 235, 0.1)", color: "#2563eb" },
          Cancelled: { bg: "rgba(239, 68, 68, 0.1)", color: "#ef4444" },
        };
        const scheme = colors[status] || colors.Pending;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Chip
              label={status}
              size="small"
              sx={{
                background: scheme.bg,
                color: scheme.color,
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
            {status !== "Completed" && (
              <Tooltip
                title={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: 16 }}
                    >
                      check_circle
                    </span>
                    Click to mark this wash as completed
                  </Box>
                }
                arrow
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={() => setCompleteWash(row)}
                  sx={{
                    color: "#10b981",
                    p: 0.5,
                    transition: "all 0.2s ease",
                    animation: "pulse-icon 2s ease-in-out infinite",
                    "@keyframes pulse-icon": {
                      "0%, 100%": { transform: "scale(1)", opacity: 0.65 },
                      "50%": { transform: "scale(1.2)", opacity: 1 },
                    },
                    "&:hover": {
                      background: "rgba(16, 185, 129, 0.15)",
                      transform: "scale(1.3)",
                      "& .status-dot": {
                        boxShadow: "0 0 12px rgba(16, 185, 129, 0.6)",
                      },
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                    <Box
                      className="status-dot"
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: "#10b981",
                        boxShadow: "0 0 6px rgba(16, 185, 129, 0.4)",
                        transition: "box-shadow 0.2s ease",
                      }}
                    />
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: 16 }}
                    >
                      check_circle
                    </span>
                  </Box>
                </IconButton>
              </Tooltip>
            )}
            {status === "Completed" && (
              <Tooltip
                title={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: 16 }}
                    >
                      undo
                    </span>
                    Revert this wash back to pending
                  </Box>
                }
                arrow
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={() => setRevertWash(row)}
                  sx={{
                    color: "#f59e0b",
                    p: 0.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: "rgba(245, 158, 11, 0.15)",
                      transform: "scale(1.3)",
                    },
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 16 }}
                  >
                    undo
                  </span>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      id: "paymentStatus",
      label: "Payment",
      minWidth: 150,
      align: "center",
      format: (_value, row) => {
        const status = row?.paymentStatus || "Pending";
        const colors: Record<string, { bg: string; color: string }> = {
          Completed: { bg: "rgba(16, 185, 129, 0.1)", color: "#10b981" },
          Pending: { bg: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" },
          Partial: { bg: "rgba(37, 99, 235, 0.1)", color: "#2563eb" },
        };
        const scheme = colors[status] || colors.Pending;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Chip
              label={status}
              size="small"
              sx={{
                background: scheme.bg,
                color: scheme.color,
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
            {status === "Pending" && (
              <Tooltip
                title={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: 16, color: "#2563eb" }}
                    >
                      payments
                    </span>
                    Complete payment
                  </Box>
                }
                arrow
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={() => setPaymentWash(row)}
                  sx={{
                    background: "rgba(37, 99, 235, 0.08)",
                    color: "#2563eb",
                    p: 0.6,
                    transition: "all 0.2s ease",
                    animation: "pulse-pay 2s ease-in-out infinite",
                    "@keyframes pulse-pay": {
                      "0%, 100%": {
                        transform: "scale(1)",
                        opacity: 0.7,
                        boxShadow: "0 0 0 0 rgba(37, 99, 235, 0)",
                      },
                      "50%": {
                        transform: "scale(1.1)",
                        opacity: 1,
                        boxShadow: "0 0 0 4px rgba(37, 99, 235, 0.1)",
                      },
                    },
                    "&:hover": {
                      background: "rgba(37, 99, 235, 0.15)",
                      transform: "scale(1.2)",
                      animation: "none",
                    },
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 17 }}
                  >
                    payments
                  </span>
                </IconButton>
              </Tooltip>
            )}
            {status === "Completed" && (
              <Tooltip
                title={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: 16, color: "#2563eb" }}
                    >
                      edit
                    </span>
                    Edit payment
                  </Box>
                }
                arrow
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={() => setEditPaymentWash(row)}
                  sx={{
                    background: "rgba(37, 99, 235, 0.08)",
                    color: "#2563eb",
                    p: 0.6,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: "rgba(37, 99, 235, 0.15)",
                      transform: "scale(1.2)",
                    },
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 17 }}
                  >
                    edit
                  </span>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      id: "images",
      label: "Images",
      minWidth: 70,
      align: "center",
      format: (_value, row) => {
        const count = row?.wash_images?.length || 0;
        return (
          <IconButton
            size="small"
            onClick={() => setImageDialogId(row.id)}
            sx={{ color: "#64748b" }}
          >
            <Badge
              badgeContent={count}
              color="primary"
              sx={{
                "& .MuiBadge-badge": { fontSize: 10, minWidth: 16, height: 16 },
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 20 }}
              >
                image
              </span>
            </Badge>
          </IconButton>
        );
      },
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 100,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/washes/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              tooltip: "Delete",
              variant: "delete",
              onClick: () => setDeleteWash(row),
            },
          ]}
        />
      ),
    },
  ];

  const dataTable = (
    <DataTable
      columns={columns}
      rows={washes}
      totalCount={totalCount}
      onPaginationChange={handlePaginationChange}
      page={page}
      limit={limit}
    />
  );

  return (
    <PageWrapper loading={loading}>
      <PageHeader
        title="Washes"
        filters={
          <>
          <DateRangeSelector
            value={dateRange}
            onChange={handleDateRangeChange}
            compact
          />
            <Box sx={{ minWidth: 220 }}>
              <AutoCompleteMultiSelect
                name="customerTypeFilter"
                label="Customer Type"
                options={customerTypes}
                value={customerTypeFilter}
                optionLabelKey="label"
                optionValueKey="value"
                onChange={(items) => {
                  setCustomerTypeFilter(items);
                  setPage(0);
                }}
              />
            </Box>
          </>
        }
        showSearch
        searchPlaceholder="Search by reg no, customer..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Wash",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/washes/create");
          },
        }}
      />

      <TabsWrapper
        defaultValue={tabValue}
        tabValueChange={(v) => {
          setPage(0);
          setTabValue(v);
          setCustomerTypeFilter(
            v === "payment-pending"
              ? customerTypes.filter((type) => type.value === "Customer")
              : customerTypes,
          );
        }}
        tabs={[
          { label: "Pending Washes", value: "pending", content: dataTable },
          { label: "Completed Washes", value: "completed", content: dataTable },
          {
            label: "Payment Pending",
            value: "payment-pending",
            content: dataTable,
          },
        ]}
      />

      <WashImageDialog
        open={imageDialogId !== null}
        washId={imageDialogId ?? 0}
        onClose={() => setImageDialogId(null)}
      />

      <CompleteWashDialog
        open={completeWash !== null}
        wash={completeWash}
        onClose={() => setCompleteWash(null)}
        onSuccess={getWashes}
      />

      <PaymentDialog
        open={paymentWash !== null}
        wash={paymentWash}
        onClose={() => setPaymentWash(null)}
        onSuccess={getWashes}
      />

      <RevertWashDialog
        open={revertWash !== null}
        wash={revertWash}
        onClose={() => setRevertWash(null)}
        onSuccess={getWashes}
      />

      <EditPaymentDialog
        open={editPaymentWash !== null}
        wash={editPaymentWash}
        onClose={() => setEditPaymentWash(null)}
        onSuccess={getWashes}
      />

      <DeleteWashDialog
        open={deleteWash !== null}
        wash={deleteWash}
        onClose={() => setDeleteWash(null)}
        onSuccess={getWashes}
      />
    </PageWrapper>
  );
}
